var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Responses403 } from "@consta/gpn-responses/Responses403";
import { Layout } from "@consta/uikit/Layout";
import { Loader } from "@consta/uikit/Loader";
import { Theme } from "@consta/uikit/Theme";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Outlet, RouterProvider } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import styles from "./App.module.scss";
import { parseAuthInfo } from "./auth-config";
import { useGetAccessList } from "./core/services/api/gateway-api/gateway-tanstack";
import { useAuthManagerStore } from "./core/services/stores/authManagerStore/store";
import { useThemeStore } from "./core/services/stores/themeStore/store";
import "./dayjs-config";
import { builderRouting } from "./routes-config";
import CustomLoader from "./shared/components/CustomLoader/CustomLoader";
import CustomSnackBar from "./shared/components/CustomSnackBar/CustomSnackBar";
import Header from "./shared/components/Header/Header";
import { roleType } from "./shared/enums/role";
import "./styles/global.scss";
import { IPageStatus } from "./types/system/i-page-ststus";
var App = function () {
    var _a = useState(false), isNeedToRefresh = _a[0], setIsNeedToRefresh = _a[1];
    var selectedTheme = useThemeStore(function (state) { return state.selectedTheme; });
    var _b = useAuthManagerStore(useShallow(function (state) { return ({
        user: state.userToken,
        permissions: state.permissions,
        currentRole: state.currentRole,
        setUserRole: state.setUserRole,
        setPermissions: state.setPermissions,
        initAuth: state.initAuth,
    }); })), user = _b.user, permissions = _b.permissions, currentRole = _b.currentRole, setUserRole = _b.setUserRole, setPermissions = _b.setPermissions, initAuth = _b.initAuth;
    var getAccessList = useGetAccessList();
    var auth = useAuth();
    var _c = useState(IPageStatus.IDLE), pageStatus = _c[0], setPageStatus = _c[1];
    useEffect(function () {
        if (user === "unauthorized") {
            setUserRole(roleType.unauthorized);
            return;
        }
        getAccessList.mutate();
    }, [user]);
    useEffect(function () {
        switch (getAccessList.status) {
            case "success": {
                var resData = getAccessList.data;
                var targetRole = resData.group;
                if (!isEqual(permissions, resData.permissions) ||
                    currentRole !== targetRole) {
                    setUserRole(targetRole);
                    setPermissions(resData.permissions);
                    setPageStatus(IPageStatus.SUCCESS);
                    setIsNeedToRefresh(!isNeedToRefresh);
                }
                return;
            }
            case "error": {
                setUserRole(roleType.unauthorized);
                setPermissions([]);
                setPageStatus(IPageStatus.ERROR);
                setIsNeedToRefresh(!isNeedToRefresh);
                return;
            }
            default:
                break;
        }
    }, [getAccessList.status, getAccessList.data]);
    useEffect(function () {
        if (auth.isAuthenticated && auth.user) {
            var authInfo = parseAuthInfo(auth.user);
            initAuth(authInfo);
        }
        else if (auth.error) {
            setPageStatus(IPageStatus.ERROR);
            setIsNeedToRefresh(!isNeedToRefresh);
        }
        else if (!auth.isLoading) {
            auth.signinRedirect();
        }
    }, [auth]);
    useEffect(function () {
        return auth.events.addSilentRenewError(function () {
            auth.revokeTokens();
        });
    }, [auth.events, auth.signinSilent]);
    var memorizedRouterProvider = useMemo(function () {
        var curRoutersConfig = builderRouting(getLayout);
        switch (pageStatus) {
            case IPageStatus.LOADING:
                return _jsx(CustomLoader, {});
            case IPageStatus.ERROR:
                return (_jsx(Layout, __assign({ className: styles.loader }, { children: _jsx(Responses403, { title: "\u041E\u0442\u0441\u0443\u0442\u0441\u0432\u0443\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F", description: "\u0423 \u0432\u0430\u0441 \u043E\u0442\u0441\u0443\u0442\u0441\u0432\u0443\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F \u043A \u0434\u0430\u043D\u043D\u043E\u0439 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435", actions: _jsx(_Fragment, {}) }) })));
            case IPageStatus.SUCCESS:
                return (_jsx(Layout, __assign({ className: styles.wrapper }, { children: _jsx(RouterProvider, { router: curRoutersConfig, fallbackElement: _jsx(Loader, { size: "m" }) }) })));
            default:
                return _jsx(CustomLoader, {});
        }
    }, [isNeedToRefresh]);
    return (_jsx(Theme, __assign({ className: styles.theme, preset: selectedTheme }, { children: memorizedRouterProvider })));
};
function getLayout(body) {
    if (body === void 0) { body = _jsx(Outlet, {}); }
    function getHeader() {
        return _jsx(Header, {});
    }
    return (_jsxs("div", __assign({ className: styles.body }, { children: [_jsx("div", __assign({ className: styles.headerContainer }, { children: getHeader() })), _jsx("div", __assign({ className: styles.bodyContainer }, { children: body })), _jsx(Layout, __assign({ className: styles.snackBar }, { children: _jsx(CustomSnackBar, {}) }))] })));
}
export default App;
